import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '../popover'
import { mergeClasses } from '../../../helpers/styling'
import { CircleAlert, Loader2Icon, Search, TriangleAlert, X } from 'lucide-react'
import { DynamicIcon } from '../../DynamicIcon'
import { SearchOption, SearchOptionGroup } from './index'
import { OptionRow, useSearchDropdownController } from './controller'
import { useVirtualizer, VirtualItem, Virtualizer } from '@tanstack/react-virtual'
import { useMemoizedFn } from "../../../hooks/use-memoized-fn"
import { useLatest } from "../../../hooks/use-latest"
import { assertNever } from '../../../utils/assert-never'

export function SearchDropdownView() {
  const ctx = useSearchDropdownController()
  const props = ctx.props

  const labelErrorMessage = ctx.selectedLabelAsync.result.type === "error"
    ? ctx.selectedLabelAsync.result.error.message
    : null
  const isFetchingError = !!props.fetchingError
  const viewParams = useSearchDropdownViewParams()

  return (
    <Popover open={ctx.open} onOpenChange={ctx.onOpen}>
      <div className={ctx.props.classNames?.commandWrapper}>
        <PopoverAnchor asChild>
          {
            // First <div> must be here directly and not in component (<SearchInput>); else bugged:
            // https://github.com/radix-ui/primitives/issues/3293
          }
          <div
            id={props.id}
            role="combobox"
            aria-expanded={ctx.open}
            title={labelErrorMessage ? `Could not load a label for value: ${labelErrorMessage}` : undefined}
            className={mergeClasses(`
              w-full flex items-center gap-2 px-2
              border border-gray-200 rounded-lg bg-white text-black transition duration-150
              focus-within:ring-2`, {
              'px-2': ctx.variant === 'big',
              'px-2 text-sm': ctx.variant === 'default',
              "bg-yellow-100": !!labelErrorMessage
            }, ctx.props.classNames?.inputWrapper)}
            input-wrapper="">
            <SearchInput />
          </div>
        </PopoverAnchor>
        {viewParams.type !== "not-visible" && (
          <PopoverContent
            className={mergeClasses('p-0 w-max max-w-[100vw] overflow-hidden', {
              "bg-red-200": isFetchingError
            }, props.classNames?.popover)}
            style={{ zIndex: 9999, minWidth: 'var(--radix-popper-anchor-width)' }}
            onOpenAutoFocus={e => e.preventDefault()}
            onFocusOutside={workaroundRadixPopoverEventIssue}
            onInteractOutside={workaroundRadixPopoverEventIssue}>
            <OptionList />
          </PopoverContent>
        )}
      </div>
    </Popover>
  )
}

function SearchInput() {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const ctx = useSearchDropdownController()

  const isLabelError = ctx.selectedLabelAsync.result.type === "error"
  const isLoading = ctx.props.isLoading || ctx.selectedLabelAsync.result.type === "loading"

  const label = ctx.selectedLabelAsync.result.type === "success"
    ? ctx.selectedLabelAsync.result.value
    : "..."
  // Only need to put the label in placeholder if syncSearchWithLabel is off
  const placeholder = ctx.props.syncSearchWithLabel
    ? ctx.labels.placeholder
    : (ctx.props.value ? label : ctx.labels.placeholder)

  useLayoutEffect(() => {
    ctx.inputRef.current = inputRef.current
    return () => {
      ctx.inputRef.current = null
    }
  })

  return (
    <>
      {isLabelError && <TriangleAlert className="py-1 text-red-800" />}

      <input
        type="text"
        ref={inputRef}
        value={ctx.searchInputValue}
        onChange={e => ctx.onSearch(e.currentTarget.value)}
        onFocus={ctx.openAndPropagate}
        placeholder={placeholder}
        className={mergeClasses(`
        w-full flex flex-grow h-10 py-3
        bg-transparent text-medium outline-none
        disabled:cursor-not-allowed disabled:opacity-50
        text-ellipsis placeholder:text-ellipsis`, {
          "placeholder:text-black": !!ctx.props.value,
          "py-3 px-2 h-10": ctx.variant === "big",
          "py-2 h-8": ctx.variant === "default",
        }, ctx.props.classNames?.input)} />

      {isLoading ? (
        <Loader2Icon
          role="status"
          aria-label="Loading"
          className="p-1 animate-spin text-wistor"
        />
      ) : (
        (ctx.searchInputValue || ctx.props.value) ? (
          <button
            type="button"
            onClick={() => {
              ctx.onSearch('')
              ctx.props.onChange(null)
            }}
            className={`opacity-75 hover:opacity-100 hover:bg-gray-400 hover:bg-opacity-50 rounded-full mr-[-.25rem]`}
            aria-label="Clear">
            <X className="p-1" />
          </button>
        ) : (
          <Search className="p-1 opacity-50" onClick={() => {
            ctx.openAndPropagate()
            inputRef.current?.focus()
          }} />
        )
      )}
    </>
  )
}

type PopoverViewParams =
  | { type: "not-visible" }
  | { type: "error", error: Error }
  | { type: "has-options", loadingMore: boolean }
  | { type: "no-options", loading: boolean }

function useSearchDropdownViewParams(): PopoverViewParams {
  const ctx = useSearchDropdownController()
  const shouldShow = ctx.search.length >= (ctx.props.minSearchLength ?? 0)
  if (!shouldShow) return { type: "not-visible" }

  const error = ctx.props.fetchingError
  if (error) return { type: "error", error }

  const loading = Boolean(ctx.props.isLoading)
  const hasOptions = ctx.derivedOptions.optionRows.length > 0
  if (hasOptions) return { type: "has-options", loadingMore: loading }

  return { type: "no-options", loading }
}

function OptionList() {
  const ctx = useSearchDropdownController()
  const viewParams = useSearchDropdownViewParams()
  if (viewParams.type === "not-visible") {
    return <div>not gonna show</div>
  }

  const showLoading
    = (viewParams.type === "no-options" && viewParams.loading)
    || (viewParams.type === "has-options" && viewParams.loadingMore)

  return (
    <div role="list"
         className={mergeClasses(
      "flex flex-col max-h-[300px] overflow-y-auto overflow-x-hidden", {
        'text-sm': ctx.variant === "default",
        'text-medium': ctx.variant === "default",
      }, ctx.props.classNames?.list)}>
      {viewParams.type === "error" && (
        <div className="font-medium w-full flex gap-2 items-center px-2 py-1.5 text-red-900">
          <CircleAlert className="py-1 text-red-800" />
          {ctx.labels.errorFetchingOptions}
        </div>
      )}
      {viewParams.type === "has-options" && (
        <OptionGroups />
      )}
      {viewParams.type === "no-options" && (
        <div className="font-medium opacity-75">
          <div role="alert" className="w-full flex gap-2 items-center px-2 py-1.5 select-none">
            {ctx.labels.noOptionsFound}
          </div>
        </div>
      )}
      {showLoading && (
        <div className="font-medium opacity-75">
          <div role="status"
               aria-busy="true"
               className="w-full flex gap-2 items-center px-2 py-1.5 select-none">
            <Loader2Icon
              role="status"
              aria-label="Loading"
              className="size-3 animate-spin text-wistor"
            />
            {ctx.labels.loadingOptions}
          </div>
        </div>
      )}
    </div>
  )
}

function OptionGroups() {
  const ctx = useSearchDropdownController()
  const ref = useRef<HTMLDivElement>(null)
  const optionRows = ctx.derivedOptions.optionRows

  const virtualizer = useVirtualizer({
    count: optionRows.length,
    getScrollElement: () => ref.current,
    estimateSize: (idx: number) => (
      optionRows[idx].type === "option"
        ? ctx.variant === "big" ? 36 : 32
        : 28
    ),
    overscan: 10
  })

  const [ activeIndex, setActiveIndex ] = useKeyboardHandling(virtualizer, optionRows)

  const renderOptionLabel = ctx.props.renderOptionLabel ?? ((option: SearchOption) =>
    <DefaultOptionItemLabel option={option}
                            search={ctx.search}
                            highlightWords={ctx.props.highlightWords} />)

  const renderGroupHeading = ctx.props.renderGroupHeading ?? ((group: SearchOptionGroup) =>
    <DefaultGroupHeading group={group} />)

  return (
    <div ref={ref} className="max-h-[300px] overflow-y-auto">
      <div className="w-full relative"
           style={{ height: `${virtualizer.getTotalSize()}px` }}>
        {/* eslint-disable-next-line array-callback-return */}
        {virtualizer.getVirtualItems().map(virtualRow => {
          const row = optionRows[virtualRow.index]
          switch (row.type) {
            case "group":
              return (
                <VirtualWrapper key={virtualRow.key}
                                virtualRow={virtualRow}
                                className="text-muted-foreground font-medium text-xs py-1.5 px-2">
                  {renderGroupHeading(row.group)}
                </VirtualWrapper>
              )
            case "option":
              const isActive = virtualRow.index === activeIndex
              const option = row.option
              return (
                <VirtualWrapper key={virtualRow.key} virtualRow={virtualRow}>
                  <div
                    role="option"
                    data-selected={isActive}
                    aria-selected={isActive}
                    onClick={() => ctx.onSelect(option.value)}
                    className={mergeClasses(
                      `relative flex items-center
                    overflow-hidden
                    cursor-default select-none text-ellipsis whitespace-nowrap
                    rounded-sm outline-none
                    px-2 py-1.5 text-sm
                    data-[disabled=true]:pointer-events-none
                    data-[disabled=true]:opacity-50
                    data-[selected=true]:bg-accent
                    data-[selected=true]:text-accent-foreground
                    data-[selected=true]:bg-indigo-100
                    `, {
                        "text-[16px]": ctx.variant === "big",
                        "bg-indigo-100": isActive,
                      }, ctx.props.classNames?.item)}
                    onPointerEnter={() => setActiveIndex(virtualRow.index)}
                  >
                    {renderOptionLabel(option)}
                  </div>
                </VirtualWrapper>
              )
            default: assertNever(row)
          }
        })}
      </div>
    </div>
  )
}

function VirtualWrapper(props: {
  virtualRow: VirtualItem
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={mergeClasses("absolute top-0 left-0 w-full will-change-transform", props.className)}
         style={{
           height: `${props.virtualRow.size}px`,
           transform: `translateY(${props.virtualRow.start}px)`
         }}>
      {props.children}
    </div>
  )
}

function useKeyboardHandling<T extends Element, U extends Element>(
  virtualizer: Virtualizer<T, U>,
  optionRows: OptionRow[]
) {
  const ctx = useSearchDropdownController()
  const latestVirtualizer = useLatest(virtualizer)

  const [ activeIndex, setActiveIndex ] = useState(0)
  const latestOptionRows = useLatest(optionRows)

  // Reset active index when options count changes
  useEffect(() => {
    setActiveIndex(latestOptionRows.current.findIndex(o => o.type === "option"))
  }, [ optionRows.length, latestOptionRows ])

  useEffect(() => {
    if (latestOptionRows.current.length === 0) return
    latestVirtualizer.current.scrollToIndex(activeIndex, { align: "auto" })
  }, [ activeIndex, latestVirtualizer, latestOptionRows ])

  const handleKeydown = useMemoizedFn((e: KeyboardEvent) => {
    if (!optionRows.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => findNextOptionIndex(optionRows, prev, 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => findNextOptionIndex(optionRows, prev, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const row = optionRows[activeIndex]
      if (!row) return
      if (row.type === "group") return
      ctx.onSelect(row.option.value)
    }
  })

  useEffect(() => {
    const input = ctx.inputRef.current
    if (!input) return
    input.addEventListener("keydown", handleKeydown)
    return () => {
      input.removeEventListener?.("keydown", handleKeydown)
    }
  }, [ ctx.inputRef, handleKeydown ])

  return [ activeIndex, setActiveIndex ] as const

  function findNextOptionIndex(
    rows: OptionRow[],
    start: number,
    direction: 1 | -1
  ) {
    let i = start + direction

    while (i >= 0 && i < rows.length) {
      if (rows[i].type === "option") return i
      i += direction
    }

    return start
  }
}

function DefaultOptionItemLabel(props: {
  option: SearchOption,
  search: string,
  highlightWords?: boolean
}) {
  return (
    <div className="h-full flex gap-1.5 items-center">
      {props.option.icon && <DynamicIcon icon={props.option.icon} size="14px" />}
      {props.highlightWords
        ? <HighlightedLabel search={props.search} label={props.option.label} />
        : props.option.label
      }
    </div>
  )
}

function DefaultGroupHeading(props: {
  group: SearchOptionGroup
}) {
  if (!props.group.icon && !props.group.label) return undefined

  return (
    <div className="h-full flex gap-2 items-center select-none">
      {/* extra div for better working alignment */}
      {props.group.icon && <div><DynamicIcon icon={props.group.icon} size="12px" /></div>}
      {props.group.label}
    </div>
  )
}

export function HighlightedLabel(props: {
  label: string
  search: string
}) {
  const index = props.label.toLowerCase().indexOf(props.search.toLowerCase())
  if (index === -1) return props.label

  const markEnd = index + props.search.length
  const start = props.label.slice(0, index)
  const marked = props.label.slice(index, markEnd)
  const end = props.label.slice(markEnd)

  return (
    <span>
      <span>{start}</span>
      <mark className="p-0">{marked}</mark>
      <span>{end}</span>
    </span>
  )
}

function workaroundRadixPopoverEventIssue(e: Event) {
  const isInCommand = e.target instanceof HTMLElement && e.target.closest('[input-wrapper]')
  if (!isInCommand) return
  e.preventDefault()
}
